import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Check, Inbox as InboxIcon, Send, X } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { DueBadge } from "@/components/work/parts";
import { listTeamData, respondAllocation, withdrawAllocation } from "@/lib/team.functions";
import { allocationStatusLabel, type AllocationRow } from "@/lib/team";
import { tagChips } from "@/lib/taxonomy";

export const Route = createFileRoute("/_authenticated/inbox")({
  head: () => ({
    meta: [
      { title: "Inbox — MyPriorities" },
      {
        name: "description",
        content:
          "Tasks other people have allocated to you: accept them into your own hub or decline with a note.",
      },
      { property: "og:title", content: "Inbox — MyPriorities" },
      {
        property: "og:description",
        content: "Accept or decline tasks allocated to you by your team.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: InboxPage,
});

function AllocationCard({ row, children }: { row: AllocationRow; children?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="font-medium">{row.title}</p>
        <Badge variant={row.status === "pending" ? "default" : "outline"}>
          {allocationStatusLabel(row.status)}
        </Badge>
      </div>
      {row.note ? <p className="mt-1 text-sm text-muted-foreground">“{row.note}”</p> : null}
      {row.desired_outcome ? (
        <p className="mt-1 text-xs text-muted-foreground">Aim: {row.desired_outcome}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary">{row.task_type}</Badge>
        <DueBadge dueDate={row.due_date} />
        <Badge variant="outline">{Number(row.hours_week)}h</Badge>
        {tagChips(row).map((tag) => (
          <Badge key={tag.id} variant="secondary" className="font-normal">
            {tag.value}
          </Badge>
        ))}
        {row.counterpart_name || row.counterpart_code ? (
          <span className="text-xs text-muted-foreground">
            {row.counterpart_name ?? row.counterpart_code}
            {row.counterpart_name && row.counterpart_code ? ` · ${row.counterpart_code}` : ""}
          </span>
        ) : null}
      </div>
      {row.response_note ? (
        <p className="mt-2 text-xs text-muted-foreground">Reply: {row.response_note}</p>
      ) : null}
      {children ? <div className="mt-3 flex flex-wrap gap-2">{children}</div> : null}
    </div>
  );
}

function InboxPage() {
  const queryClient = useQueryClient();
  const fetchTeam = useServerFn(listTeamData);
  const respond = useServerFn(respondAllocation);
  const withdraw = useServerFn(withdrawAllocation);

  const team = useQuery({ queryKey: ["team-data"], queryFn: () => fetchTeam() });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["team-data"] });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };

  const answer = useMutation({
    mutationFn: (input: { id: string; accept: boolean }) =>
      respond({ data: { ...input, note: null } }),
    onSuccess: (result, input) => {
      if (result && "error" in result) {
        toast.error(result.error);
        return;
      }
      toast.success(input.accept ? "Added to your hub" : "Declined");
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const pull = useMutation({
    mutationFn: (id: string) => withdraw({ data: { id } }),
    onSuccess: () => {
      toast.success("Allocation withdrawn");
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const inbox = team.data?.inbox ?? [];
  const sent = team.data?.sent ?? [];
  const pending = inbox.filter((a) => a.status === "pending");
  const answered = inbox.filter((a) => a.status !== "pending");

  return (
    <AppShell title="Inbox" subtitle="Tasks people have allocated to you.">
      {team.data?.myCode ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Share your access code <span className="font-medium">{team.data.myCode}</span> so
          colleagues can allocate work to you.
        </p>
      ) : null}

      <CollapsibleSection
        storageKey="inbox-pending"
        title="Waiting on you"
        description="Accepting copies the task into your own hub, hours and all."
        summary={`${pending.length} to answer`}
      >
        <div className="panel mt-3 space-y-2 p-5">
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              <InboxIcon className="mr-1 inline size-4" /> Nothing waiting.
            </p>
          ) : (
            pending.map((row) => (
              <AllocationCard key={row.id} row={row}>
                <Button
                  size="sm"
                  onClick={() => answer.mutate({ id: row.id, accept: true })}
                  disabled={answer.isPending}
                >
                  <Check className="size-4" /> Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => answer.mutate({ id: row.id, accept: false })}
                  disabled={answer.isPending}
                >
                  <X className="size-4" /> Decline
                </Button>
              </AllocationCard>
            ))
          )}
        </div>
      </CollapsibleSection>

      {answered.length ? (
        <CollapsibleSection
          storageKey="inbox-answered"
          title="Already answered"
          summary={`${answered.length} handled`}
        >
          <div className="panel mt-3 space-y-2 p-5">
            {answered.map((row) => (
              <AllocationCard key={row.id} row={row} />
            ))}
          </div>
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection
        storageKey="inbox-sent"
        title="Allocated by you"
        description="Work you've offered to other accounts."
        summary={`${sent.length} sent`}
      >
        <div className="panel mt-3 space-y-2 p-5">
          {sent.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              <Send className="mr-1 inline size-4" /> Use the send icon on any task to allocate it.
            </p>
          ) : (
            sent.map((row) => (
              <AllocationCard key={row.id} row={row}>
                {row.status === "pending" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => pull.mutate(row.id)}
                    disabled={pull.isPending}
                  >
                    Withdraw
                  </Button>
                ) : null}
              </AllocationCard>
            ))
          )}
        </div>
      </CollapsibleSection>
    </AppShell>
  );
}
